import React, { useEffect } from 'react'
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom'

import './styles/App.scss'
import { useUser } from './contexts/userContext'
import { listenForAuthStateChanges } from './services/auth'
import { identifyUser, trackPageView } from './services/analytics'
import { callCompleteOnboarding } from './components/slides/common'

import CongratsCommunitySlide from './components/CongratsCommunitySlide'
import TermsSlide from './components/TermsSlide'
import SignInUpSlide from './components/slides/SignInUpSlide'
import AboutUsSlide from './components/slides/AboutUsSlide'
import OurDAOSlide from './components/slides/OurDAOSlide'
import OurProductsSlide from './components/slides/OurProductsSlide'
import AboutYouSlide from './components/slides/AboutYouSlide'
import LetsConnectSlide from './components/slides/LetsConnectSlide'
import WhyJoinSlide from './components/slides/WhyJoinSlide'
import YourSocialsSlide from './components/slides/YourSocialsSlide'
import YourWorkingGroupSlide from './components/slides/YourWorkingGroupSlide'
import WorkingGroupContributionSlide from './components/slides/WorkingGroupContributionSlide'
import YourSkillsEngineerSlide from './components/slides/YourSkillsEngineerSlide'
import YourExperienceEngineerSlide from './components/RoleSlide'
import BookCallEngineerSlide from './components/slides/BookCallEngineerSlide'
import BookCallSlide from './components/slides/BookCallSlide'
import CongratsSlide from './components/CongratsSlide'
import YourMemberStatusSlide from './components/slides/YourMemberStatusSlide'

const TOTAL_STEPS = 12 // Total onboarding steps

function App () {
  const { user, setUser } = useUser()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  // Start listening for auth changes on page load
  useEffect(
    () =>
      listenForAuthStateChanges(
        setUser,
        signedInUser => {
          const { onboardingStage, uid, email } = signedInUser

          identifyUser(uid, { email })

          if (onboardingStage) {
            // Navigate to slide left off as soon as user signs in
            // navigate(onboardingStage, { replace: true })
          }
        },
        () => {
          // Navigate to auth screen (home) if user is not signed in/signs out
          navigate('/', { replace: true })
        }
      ),
    []
  )

  // Listen to navigation changes
  useEffect(() => {
    // To track manually page views in our SPA as posthog doesn't
    // https://posthog.com/docs/integrate/client/js#one-page-apps-and-page-views
    trackPageView()
  }, [pathname])

  return (
    <Routes>
      <Route
        path='/'
        element={
          <SignInUpSlide
            step={1}
            totalSteps={TOTAL_STEPS}
            nextTo='/about-us'
            noBack
          />
        }
      />
      <Route
        path='/about-us'
        element={
          <AboutUsSlide
            step={2}
            totalSteps={TOTAL_STEPS}
            nextTo='/our-dao'
            backTo='/'
          />
        }
      />
      <Route
        path='/our-dao'
        element={
          <OurDAOSlide
            step={3}
            totalSteps={TOTAL_STEPS}
            nextTo='/our-products'
            backTo='/about-us'
          />
        }
      />
      <Route
        path='/our-products'
        element={
          <OurProductsSlide
            step={4}
            totalSteps={TOTAL_STEPS}
            nextTo='/your-membership'
            backTo='/our-dao'
          />
        }
      />
      <Route
        path='/your-membership'
        element={
          <YourMemberStatusSlide
            step={6}
            totalSteps={TOTAL_STEPS}
            nextTo='/about-you'
            backTo='/our-products'
          />
        }
      />
      <Route
        path='/about-you'
        element={
          <AboutYouSlide
            step={7}
            totalSteps={TOTAL_STEPS}
            nextTo='/lets-connect'
            backTo='/your-membership'
          />
        }
      />
      <Route
        path='/lets-connect'
        element={
          <LetsConnectSlide
            step={8}
            totalSteps={TOTAL_STEPS}
            nextTo={() =>
              user?.memberStatus === 'Community Member'
                ? navigate('/book-call')
                : navigate('/why-join')
            }
            onFormSubmit={async () =>
              user?.memberStatus === 'Community Member' &&
              (await callCompleteOnboarding(user)())
            }
            backTo='/about-you'
          />
        }
      />
      <Route
        path='/why-join'
        element={
          <WhyJoinSlide
            step={9}
            totalSteps={TOTAL_STEPS}
            nextTo={() =>
              user?.memberStatus === 'Ambassador'
                ? navigate('/book-call')
                : navigate('/your-socials')
            }
            onFormSubmit={async () =>
              user?.memberStatus === 'Ambassador' &&
              (await callCompleteOnboarding(user)())
            }
            backTo='/lets-connect'
          />
        }
      />
      <Route
        path='/your-socials'
        element={
          <YourSocialsSlide
            step={10}
            totalSteps={TOTAL_STEPS}
            nextTo='/your-group'
            backTo='/why-join'
          />
        }
      />
      <Route
        path='/your-group'
        element={
          <YourWorkingGroupSlide
            step={11}
            totalSteps={TOTAL_STEPS}
            nextTo={() => {}}
            backTo='/your-socials'
            onFormSubmit={values =>
              values?.workingGroup === 'Tech Support'
                ? navigate('/your-tech-skills')
                : navigate('/your-skills')
            }
          />
        }
      />
      <Route
        path='/your-skills'
        element={
          <WorkingGroupContributionSlide
            step={13}
            totalSteps={TOTAL_STEPS}
            nextTo='/book-call'
            backTo='/your-group'
          />
        }
      />
      <Route
        path='/your-tech-skills'
        element={
          <YourSkillsEngineerSlide
            step={12}
            totalSteps={TOTAL_STEPS}
            nextTo='/your-tech-experience'
            backTo='/your-group'
          />
        }
      />
      <Route
        path='/your-tech-experience'
        element={
          <YourExperienceEngineerSlide
            step={14}
            totalSteps={TOTAL_STEPS}
            onFormSubmit={callCompleteOnboarding(user)}
            nextButtonText='Submit'
            nextTo='/book-tech-call'
            backTo='/your-tech-skills'
          />
        }
      />
      <Route
        path='/book-tech-call'
        element={<BookCallEngineerSlide nextTo='/congrats' noBack />}
      />
      <Route
        path='/book-call'
        element={
          <BookCallSlide
            nextTo={() =>
              user?.memberStatus === 'Contributor'
                ? navigate('/congrats')
                : navigate('/congrats-community')
            }
            noBack
          />
        }
      />
      <Route path='/congrats' element={<CongratsSlide />} />
      <Route path='/congrats-community' element={<CongratsCommunitySlide />} />
      <Route path='/terms' element={<TermsSlide />} />
      <Route path='/*' element={<Navigate to='/' replace={true} />} />
    </Routes>
  )
}

export default App
